/*
 *
 * SettingsAlertPopup
 *
 * @version 1.0.0
 * @author Ivana Piscitelli
 * @since 24-Apr-2025
*/
import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalLoader from '@nexidigital/portalloader'
import { useIntl } from 'react-intl'
import PortalSwitch from '@nexidigital/portalswitch'
import { useSettingsAlertPopupMap } from './SettingsAlertPopupMap'
import Toggle from 'Hoc/ToggleComponent'
import componentConfig from './componentConfig'
import { nullSafe } from 'Utility/globalMethods'
import './SettingsAlertPopup.scss'

export const mapStatusByKey = (array) => array.filter(item => item.key !== 'ALL')
  .map(item => ({
    type: item.key,
    action: item.status ? 'ENABLE' : 'DISABLE'
  }))

const SettingsAlertPopup = (props) => {
  const intl = useIntl()
  const serviceAlert = nullSafe(() => componentConfig.settingAlert.serviceAlert(), false)
  const [settingsAlerts, setSettingAlerts] = useState([])
  const isFirstRender = useRef(true)

  useEffect(() => {
    props.actions.doGetAlertsStatus()
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (Object.keys(props.alertsStatus).length > 0) {
      const newAlerts = useSettingsAlertPopupMap({ ...props.alertsStatus }, intl, serviceAlert, props.hasReportPermission, props.alertFeatures)
      setSettingAlerts(newAlerts)
    }
  }, [props.alertsStatus, props.alertFeatures, props.hasReportPermission])

  const handleSwitch = (key) => {
    let alertsArray = [...settingsAlerts]
    const newAlert = [...settingsAlerts]
    const indexKey = newAlert.findIndex(alert => alert.key === key)

    // Solo questi item sono controllabili da ALL
    const controllable = (arr) => arr.filter(alert => alert.key !== 'ALL' && alert.key !== 'SERVICE_ALERTS')

    if (key !== 'ALL') {
      newAlert[indexKey].status = !alertsArray[indexKey].status
      alertsArray = newAlert

      // set ALL as false se tutti i controllable sono false
      if (controllable(alertsArray).every(alert => !alert.status)) {
        const indexAll = newAlert.findIndex(alert => alert.key === 'ALL')
        newAlert[indexAll].status = false
        alertsArray = newAlert
      }

      // set ALL as true se almeno un controllable è true
      if (controllable(alertsArray).some(alert => alert.status) && !alertsArray.find(alert => alert.key === 'ALL').status) {
        const indexAll = newAlert.findIndex(alert => alert.key === 'ALL')
        newAlert[indexAll].status = true
        alertsArray = newAlert
      }
    } else {
      const allStatus = !alertsArray[indexKey].status
      newAlert[indexKey].status = allStatus

      // Aggiorna solo i controllable, SERVICE_ALERTS rimane invariato
      for (let i = 0; i < newAlert.length; i++) {
        if (newAlert[i].key !== 'ALL' && newAlert[i].key !== 'SERVICE_ALERTS') {
          newAlert[i].status = allStatus
        }
      }
      alertsArray = newAlert
    }

    setSettingAlerts(alertsArray)
  }

  const onConfirm = () => {
    const payload = {
      settings: mapStatusByKey(settingsAlerts)
    }
    props.actions.doPutAlertsStatus(payload)
    props.actions.doHidePopup()
  }

  return (
    <Toggle>
      <MerchantModal
        onClose={() => props.actions.doHidePopup()}
        primaryAction={() => onConfirm()}
        primaryButtonLabel={intl.formatMessage({ id: 't.app.confirm' })}
        title={intl.formatMessage({ id: 't.app.settingsalert.title.popup' })}
      >
        {(props.isLoading || settingsAlerts.length === 0)
          ? <PortalLoader circular centered />
          : (
            <div className='alertBox'>
              {settingsAlerts.filter((alert) => alert.key === 'ALL').map((alert, index) => (
                <div key={index} className='alertContent'>
                  <div className='alertText'>
                    <div className='alertHeader'>{alert.header}</div>
                    <div className='alertDesc'>{alert.description}</div>
                  </div>
                  <PortalSwitch
                    disabled={alert.disabled}
                    onChange={() => handleSwitch(alert.key)}
                    checked={alert.status}
                  />
                </div>
              ))}
              {settingsAlerts.filter((alert) => alert.key !== 'ALL').map((alert, index) => (
                <div key={index} className='alertContent'>
                  <div className='alertText'>
                    <div className='alertHeader'>{alert.header}</div>
                    <div className='alertDesc'>{alert.description}</div>
                  </div>
                  <PortalSwitch
                    disabled={alert.disabled}
                    onChange={() => handleSwitch(alert.key)}
                    checked={alert.status}
                  />
                </div>
              ))}
            </div>)}
      </MerchantModal>
    </Toggle>
  )
}

SettingsAlertPopup.propTypes = {
  actions: PropTypes.object,
  alertsStatus: PropTypes.object,
  isLoading: PropTypes.bool,
  hasReportPermission: PropTypes.bool,
  alertFeatures: PropTypes.arrayOf(PropTypes.string)
}

SettingsAlertPopup.defaultProps = {
  action: {},
  alertsStatus: {},
  isLoading: false,
  hasReportPermission: false
}

export default SettingsAlertPopup
